import { Injectable, Injector, inject } from '@angular/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { firstValueFrom } from 'rxjs';
import { SettingService } from './setting.service';

/**
 * Executes reCAPTCHA v3 only when the `captcha` setting is enabled (default off). The backend
 * checks the same setting, so a skipped token is accepted exactly when verification is off.
 * The reCAPTCHA service is resolved lazily so apps without `RecaptchaV3Module` still work.
 *
 * The setting is readable by the `anonymous` role only, so a signed in user (e.g. /auth/mobile-verification)
 * gets no row or an error; a token is fetched anyway since the backend ignores it when verification is off.
 */
@Injectable({ providedIn: 'root' })
export class CaptchaService {
  private injector = inject(Injector);
  private setting = inject(SettingService);
  private enabled: Promise<boolean> | undefined;

  isEnabled(): Promise<boolean> {
    this.enabled ??= this.setting
      .getSettingEnabled('captcha')
      .then((enabled) => enabled ?? true)
      .catch(() => true);
    return this.enabled;
  }

  /**
   * Returns a token for the action, or '' when captcha is disabled
   */
  async getToken(action: string): Promise<string> {
    if (!(await this.isEnabled())) {
      return '';
    }
    return firstValueFrom(this.injector.get(ReCaptchaV3Service).execute(action));
  }
}
